import { hydrateBackgroundLinks, restoreBackgroundLinkOverrides } from '../logic/background_links.js';

const PREFERENCES_KEY = "model-vista.experimental.preferences.v1";
const LIBRARY_DATABASE_NAME = "model-vista-user-libraries";
const LIBRARY_STORE_NAME = "libraries";
const LIBRARY_DATABASE_VERSION = 1;

export const EXPERIMENTAL_PREFERENCE_WATCH_KEYS = [
    "ibl",
    "iblIntensity",
    "gtaoEnabled",
    "gtaoIntensity",
    "aoMethod",
    "blurEnvAmount",
    "punctualLights",
    "renderEnv",
    "clearColor",
    "exposureSetting",
    "lightDirectionDegrees",
    "lightHeight",
    "shadowStrength",
    "toneMap",
    "selectedEnvironmentRotation",
    "perspectiveLinesEnabled",
    "perspectiveLinesHeight",
    "cameraProjectionMode",
    "fovDegrees",
    "orthographicSize",
    "modelTransformMode",
    "experimentalLeftPanel",
    "experimentalRightPanel",
    "experimentalInspectorTab",
    "experimentalLeftPanelCollapsed",
    "experimentalRightPanelCollapsed",
    "experimentalLeftPanelWidth",
    "experimentalRightPanelWidth",
    "selectedEnvironmentPreference",
    "selectedModelLibraryId",
    "selectedBackgroundLibraryId"
];

const clamp = (value, minimum, maximum, fallback) => {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return fallback;
    }
    return Math.max(minimum, Math.min(maximum, number));
};

const boolean = (value, fallback) =>
    typeof value === "boolean" ? value : fallback;

const choice = (value, choices, fallback) =>
    choices.includes(value) ? value : fallback;

const identifier = (value, fallback) => {
    if (typeof value !== "string") {
        return fallback;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 && trimmed.length <= 300 ? trimmed : fallback;
};

const hexColor = (value, fallback) =>
    typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value)
        ? value.toLowerCase()
        : fallback;

export function getExperimentalPreferenceDefaults(viewportWidth = 1280) {
    return {
        ibl: true,
        iblIntensity: 0,
        gtaoEnabled: false,
        gtaoIntensity: 1,
        aoMethod: "gtao-quarter",
        blurEnvAmount: 70,
        punctualLights: true,
        renderEnv: true,
        clearColor: "#303542",
        exposureSetting: 0,
        lightDirectionDegrees: 35,
        lightHeight: 60,
        shadowStrength: 1,
        toneMap: "Khronos PBR Neutral",
        selectedEnvironmentRotation: "+Z",
        perspectiveLinesEnabled: true,
        perspectiveLinesHeight: 0,
        cameraProjectionMode: "perspective",
        fovDegrees: 60,
        orthographicSize: 6,
        modelTransformMode: "move",
        experimentalLeftPanel: "models",
        experimentalRightPanel: "properties",
        experimentalInspectorTab: "states",
        experimentalLeftPanelCollapsed: true,
        experimentalRightPanelCollapsed: true,
        experimentalLeftPanelWidth: Math.max(
            360,
            Math.min(720, Math.round(Number(viewportWidth || 1280) * 0.3))
        ),
        experimentalRightPanelWidth: 480,
        selectedEnvironment: "Cannon_Exterior",
        selectedModelLibraryId: "bundled",
        selectedBackgroundLibraryId: "bundled"
    };
}

export function sanitizeExperimentalPreferences(value, viewportWidth = 1280) {
    const defaults = getExperimentalPreferenceDefaults(viewportWidth);
    const source = value && typeof value === "object" ? value : {};
    return {
        aoMethod: ["gtao", "gtao-quarter", "ssao", "n8ao"].includes(source.aoMethod) ? source.aoMethod : defaults.aoMethod,
        gtaoEnabled: boolean(source.gtaoEnabled, defaults.gtaoEnabled),
        gtaoIntensity: clamp(source.gtaoIntensity, 0, 2, defaults.gtaoIntensity),
        ibl: boolean(source.ibl, defaults.ibl),
        iblIntensity: clamp(source.iblIntensity, -2, 5, defaults.iblIntensity),
        blurEnvAmount: clamp(source.blurEnvAmount, 0, 100, defaults.blurEnvAmount),
        punctualLights: boolean(source.punctualLights, defaults.punctualLights),
        renderEnv: boolean(source.renderEnv, defaults.renderEnv),
        clearColor: hexColor(source.clearColor, defaults.clearColor),
        exposureSetting: clamp(
            source.exposureSetting,
            -6,
            21,
            defaults.exposureSetting
        ),
        lightDirectionDegrees: clamp(
            source.lightDirectionDegrees,
            0,
            360,
            defaults.lightDirectionDegrees
        ),
        lightHeight: clamp(source.lightHeight, 5, 90, defaults.lightHeight),
        shadowStrength: clamp(source.shadowStrength, 0, 1, defaults.shadowStrength),
        toneMap: choice(source.toneMap, [
            "Khronos PBR Neutral",
            "ACES Filmic Tone Mapping (Hill - Exposure Boost)",
            "ACES Filmic Tone Mapping (Narkowicz)",
            "ACES Filmic Tone Mapping (Hill)",
            "None (Linear mapping, clamped at 1.0)"
        ], defaults.toneMap),
        selectedEnvironmentRotation: choice(
            source.selectedEnvironmentRotation,
            ["+Z", "-X", "-Z", "+X"],
            defaults.selectedEnvironmentRotation
        ),
        perspectiveLinesEnabled: boolean(
            source.perspectiveLinesEnabled,
            defaults.perspectiveLinesEnabled
        ),
        perspectiveLinesHeight: clamp(
            source.perspectiveLinesHeight,
            -100,
            100,
            defaults.perspectiveLinesHeight
        ),
        cameraProjectionMode: choice(
            source.cameraProjectionMode,
            ["perspective", "orthographic"],
            defaults.cameraProjectionMode
        ),
        fovDegrees: clamp(source.fovDegrees, 30, 90, defaults.fovDegrees),
        orthographicSize: clamp(
            source.orthographicSize,
            0.1,
            100,
            defaults.orthographicSize
        ),
        modelTransformMode: choice(
            source.modelTransformMode,
            ["none", "move", "rotate", "scale"],
            defaults.modelTransformMode
        ),
        experimentalLeftPanel: choice(
            source.experimentalLeftPanel,
            ["models", "backgrounds", "scene"],
            defaults.experimentalLeftPanel
        ),
        experimentalRightPanel: choice(
            source.experimentalRightPanel,
            ["properties", "camera", "visuals", "annotate", "ai", "system"],
            defaults.experimentalRightPanel
        ),
        experimentalInspectorTab: choice(
            source.experimentalInspectorTab,
            ["transform", "states", "analyze"],
            defaults.experimentalInspectorTab
        ),
        experimentalLeftPanelCollapsed: boolean(
            source.experimentalLeftPanelCollapsed,
            defaults.experimentalLeftPanelCollapsed
        ),
        experimentalRightPanelCollapsed: boolean(
            source.experimentalRightPanelCollapsed,
            defaults.experimentalRightPanelCollapsed
        ),
        experimentalLeftPanelWidth: clamp(
            source.experimentalLeftPanelWidth,
            240,
            720,
            defaults.experimentalLeftPanelWidth
        ),
        experimentalRightPanelWidth: clamp(
            source.experimentalRightPanelWidth,
            320,
            772,
            defaults.experimentalRightPanelWidth
        ),
        selectedEnvironment: identifier(
            source.selectedEnvironment,
            defaults.selectedEnvironment
        ),
        selectedModelLibraryId: identifier(
            source.selectedModelLibraryId,
            defaults.selectedModelLibraryId
        ),
        selectedBackgroundLibraryId: identifier(
            source.selectedBackgroundLibraryId,
            defaults.selectedBackgroundLibraryId
        )
    };
}

export function loadExperimentalPreferences(storage = globalThis.localStorage, viewportWidth = 1280) {
    if (!storage) {
        return {
            restored: false,
            values: getExperimentalPreferenceDefaults(viewportWidth)
        };
    }
    try {
        const serialized = storage.getItem(PREFERENCES_KEY);
        if (!serialized) {
            return {
                restored: false,
                values: getExperimentalPreferenceDefaults(viewportWidth)
            };
        }
        return {
            restored: true,
            values: sanitizeExperimentalPreferences(JSON.parse(serialized), viewportWidth)
        };
    } catch (error) {
        console.warn("Model Vista: Unable to restore viewer settings.", error);
        return {
            restored: false,
            values: getExperimentalPreferenceDefaults(viewportWidth)
        };
    }
}

export function captureExperimentalPreferences(state, viewportWidth = 1280) {
    const values = {};
    for (const key of EXPERIMENTAL_PREFERENCE_WATCH_KEYS) {
        values[key] = state?.[key];
    }
    values.selectedEnvironment = state?.selectedEnvironmentPreference;
    return sanitizeExperimentalPreferences(values, viewportWidth);
}

export function saveExperimentalPreferences(state, storage = globalThis.localStorage) {
    if (!storage) {
        return false;
    }
    try {
        storage.setItem(
            PREFERENCES_KEY,
            JSON.stringify(captureExperimentalPreferences(state, globalThis.innerWidth))
        );
        return true;
    } catch (error) {
        console.warn("Model Vista: Unable to save viewer settings.", error);
        return false;
    }
}

export function clearExperimentalPreferences(storage = globalThis.localStorage) {
    try {
        storage?.removeItem(PREFERENCES_KEY);
        return true;
    } catch (error) {
        console.warn("Model Vista: Unable to reset viewer settings.", error);
        return false;
    }
}

const normalizePath = (path) => String(path || "")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")
    .replace(/\/+/g, "/");

const basename = (path) => normalizePath(path).split("/").pop() || "";
const dirname = (path) => {
    const normalized = normalizePath(path);
    const slash = normalized.lastIndexOf("/");
    return slash === -1 ? "" : normalized.slice(0, slash + 1);
};
const withoutExtension = (path) => {
    const dot = path.lastIndexOf(".");
    return dot === -1 ? path : path.slice(0, dot);
};
const extension = (path) => {
    const name = basename(path);
    const dot = name.lastIndexOf(".");
    return dot === -1 ? "" : name.slice(dot).toLowerCase();
};
const displayName = (path) => withoutExtension(basename(path)) || "Untitled";

const createId = (prefix) => {
    const randomId = globalThis.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    return `${prefix}-${randomId}`;
};

const inferRootName = (files) => {
    const firstEntry = files?.[0];
    const firstFile = firstEntry?.file || firstEntry;
    const firstPath = normalizePath(
        firstEntry?.relativePath || firstFile?.webkitRelativePath || ""
    );
    return firstPath.includes("/") ? firstPath.split("/")[0] : "";
};

const stripRootFolder = (path, rootName) => {
    const normalized = normalizePath(path);
    const prefix = rootName ? `${rootName}/` : "";
    return prefix && normalized.startsWith(prefix)
        ? normalized.slice(prefix.length)
        : normalized;
};

const isThumbnailImage = (path) =>
    [".jpg", ".jpeg", ".png", ".webp"].includes(extension(path));

const findThumbnailPath = (records, mainPath) => {
    const mainDirectory = dirname(mainPath);
    const mainStem = withoutExtension(mainPath).toLowerCase();
    const exact = records.find(record =>
        isThumbnailImage(record.path) &&
        withoutExtension(record.path).toLowerCase() === mainStem
    );
    if (exact) {
        return exact.path;
    }
    const screenshot = records.find(record =>
        isThumbnailImage(record.path) &&
        record.path.toLowerCase() === `${mainDirectory}screenshot/screenshot.png`.toLowerCase()
    );
    if (screenshot) {
        return screenshot.path;
    }
    return null;
};

export function createUserLibraryRecord(kind, fileList, options = {}) {
    if (kind !== "model" && kind !== "background") {
        throw new Error("Library type must be model or background.");
    }
    const fileEntries = Array.from(fileList || []);
    if (fileEntries.length === 0) {
        throw new Error("Choose a folder containing supported assets.");
    }
    const inferredRootName = inferRootName(fileEntries);
    const name = String(options.name || inferredRootName ||
        (kind === "model" ? "Model Library" : "Background Library")).trim();
    const records = fileEntries.map(entry => {
        const file = entry?.file || entry;
        const sourcePath = entry?.relativePath || file.webkitRelativePath || file.name;
        const path = stripRootFolder(sourcePath, inferredRootName) || file.name;
        return {
            path: normalizePath(path),
            name: file.name || basename(path),
            type: file.type || "application/octet-stream",
            size: Number(file.size) || 0,
            lastModified: Number(file.lastModified) || 0,
            blob: file
        };
    });
    const supportedExtensions = kind === "model"
        ? [".glb", ".gltf", ".fbx"]
        : [".hdr", ".glb"];
    const mainFiles = records.filter(record =>
        supportedExtensions.includes(extension(record.path))
    );
    if (mainFiles.length === 0) {
        throw new Error(kind === "model"
            ? "No GLB, glTF, or FBX files were found in that folder."
            : "No HDR or GLB files were found in that folder.");
    }
    const assets = mainFiles.map((record, index) => ({
        id: `${index}-${encodeURIComponent(record.path.toLowerCase())}`,
        name: displayName(record.path),
        mainPath: record.path,
        thumbnailPath: findThumbnailPath(records, record.path)
    }));
    return {
        id: options.id || createId(kind),
        kind,
        name: name || (kind === "model" ? "Model Library" : "Background Library"),
        storageMode: options.storageMode || (options.directoryHandle ? "directory" : "copy"),
        directoryHandle: options.directoryHandle || null,
        permissionState: options.directoryHandle ? "granted" : "local-copy",
        createdAt: options.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        files: records,
        assets
    };
}

const decodeBase64Image = (value, fallbackMimeType = "image/png") => {
    if (typeof value !== "string" || value.length === 0) {
        return null;
    }
    let base64 = value;
    let mimeType = fallbackMimeType;
    const dataUrl = value.match(/^data:([^;,]+);base64,(.*)$/s);
    if (dataUrl) {
        mimeType = dataUrl[1] || mimeType;
        base64 = dataUrl[2];
    } else if (base64.startsWith("/9j/")) {
        mimeType = "image/jpeg";
    } else if (base64.startsWith("UklGR")) {
        mimeType = "image/webp";
    }
    try {
        const binary = globalThis.atob(base64.replace(/\s/g, ""));
        const bytes = new Uint8Array(binary.length);
        for (let index = 0; index < binary.length; index++) {
            bytes[index] = binary.charCodeAt(index);
        }
        return new Blob([bytes], {type: mimeType});
    } catch {
        return null;
    }
};

const readGlbJson = async (blob) => {
    if (!blob?.slice || blob.size < 20) {
        return null;
    }
    const header = new DataView(await blob.slice(0, 20).arrayBuffer());
    if (header.getUint32(0, true) !== 0x46546c67) {
        return null;
    }
    const jsonLength = header.getUint32(12, true);
    if (header.getUint32(16, true) !== 0x4e4f534a || jsonLength <= 0) {
        return null;
    }
    const jsonBytes = await blob.slice(20, 20 + jsonLength).arrayBuffer();
    const jsonText = new TextDecoder().decode(jsonBytes).trimEnd();
    return {
        json: JSON.parse(jsonText),
        jsonLength
    };
};

const glbBufferViewImage = async (blob, packageData, image) => {
    if (!Number.isInteger(image?.bufferView)) {
        return null;
    }
    const bufferView = packageData.json?.bufferViews?.[image.bufferView];
    if (!bufferView || (bufferView.buffer ?? 0) !== 0) {
        return null;
    }
    const binaryHeaderOffset = 20 + packageData.jsonLength;
    if (blob.size < binaryHeaderOffset + 8) {
        return null;
    }
    const binaryHeader = new DataView(
        await blob.slice(binaryHeaderOffset, binaryHeaderOffset + 8).arrayBuffer()
    );
    if (binaryHeader.getUint32(4, true) !== 0x004e4942) {
        return null;
    }
    const byteOffset = Number(bufferView.byteOffset) || 0;
    const byteLength = Number(bufferView.byteLength) || 0;
    if (byteLength <= 0) {
        return null;
    }
    const start = binaryHeaderOffset + 8 + byteOffset;
    return blob.slice(start, start + byteLength, image.mimeType || "image/png");
};

export async function extractEmbeddedModelThumbnail(blob) {
    try {
        const packageData = await readGlbJson(blob);
        if (!packageData) {
            return null;
        }
        const extensionData = packageData.json?.extensions?.INNOWORKS_configurables;
        if (!extensionData) {
            return null;
        }
        const directThumbnail = decodeBase64Image(extensionData.AssetThumbnailBase64);
        if (directThumbnail) {
            return directThumbnail;
        }
        const imageIndex = extensionData.AssetThumbnailImageIndex;
        if (!Number.isInteger(imageIndex)) {
            return null;
        }
        const embeddedRecord = (extensionData.EmbeddedImages || []).find(record =>
            record?.ImageIndex === imageIndex
        );
        const recordThumbnail = decodeBase64Image(
            embeddedRecord?.Base64,
            embeddedRecord?.MimeType || "image/png"
        );
        if (recordThumbnail) {
            return recordThumbnail;
        }
        const image = packageData.json?.images?.[imageIndex];
        if (typeof image?.uri === "string") {
            return decodeBase64Image(image.uri, image.mimeType || "image/png");
        }
        return glbBufferViewImage(blob, packageData, image);
    } catch (error) {
        console.warn("Model Vista: Unable to read an embedded library thumbnail.", error);
        return null;
    }
}

export async function hydrateUserLibraryThumbnails(library) {
    if (!library || (library.kind !== "model" && library.kind !== "background")) {
        return library;
    }
    await hydrateBackgroundLinks(library);
    for (const asset of library.assets || []) {
        if (asset.thumbnailPath && !asset.cachedThumbnailBlob) {
            const thumbnailFile = (library.files || []).find(file =>
                file.path === asset.thumbnailPath
            );
            if (thumbnailFile?.blob) {
                asset.cachedThumbnailBlob = thumbnailFile.blob;
            }
        }
        if (
            asset.thumbnailPath ||
            asset.cachedThumbnailBlob ||
            asset.embeddedThumbnailBlob
        ) {
            continue;
        }
        const mainFile = (library.files || []).find(file => file.path === asset.mainPath);
        if (extension(asset.mainPath) !== ".glb" || !mainFile?.blob) {
            continue;
        }
        asset.embeddedThumbnailBlob = await extractEmbeddedModelThumbnail(mainFile.blob);
    }
    return library;
}

const libraryForStorage = (library) => ({
    id: library.id,
    kind: library.kind,
    name: String(library.name || "Library").trim() || "Library",
    storageMode: library.storageMode === "directory" ? "directory" : "copy",
    directoryHandle: library.storageMode === "directory"
        ? library.directoryHandle || null
        : null,
    createdAt: library.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    files: (library.files || []).map(file => {
        const record = {
            path: file.path,
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified
        };
        if (library.storageMode !== "directory") {
            record.blob = file.blob;
        }
        return record;
    }),
    assets: (library.assets || []).map(asset => ({
        id: asset.id,
        name: asset.name,
        mainPath: asset.mainPath,
        thumbnailPath: asset.thumbnailPath || null,
        cachedThumbnailBlob: asset.cachedThumbnailBlob || null,
        embeddedThumbnailBlob: asset.embeddedThumbnailBlob || null,
        backgroundLink: asset.backgroundLink ? {
            path: asset.backgroundLink.path || null,
            sourceKey: asset.backgroundLink.sourceKey || null,
            key: asset.backgroundLink.key || null,
            displayName: asset.backgroundLink.displayName || null,
            proceduralSunEnabled: asset.backgroundLink.proceduralSunEnabled !== false
        } : null,
        backgroundLinkOverride: asset.backgroundLinkOverride === true
    }))
});

export async function collectFilesFromDirectoryHandle(directoryHandle) {
    if (!directoryHandle || directoryHandle.kind !== "directory") {
        throw new Error("Choose a local folder.");
    }
    const files = [];
    const walk = async (handle, relativeDirectory = "") => {
        for await (const [name, entry] of handle.entries()) {
            const relativePath = relativeDirectory
                ? `${relativeDirectory}/${name}`
                : name;
            if (entry.kind === "directory") {
                await walk(entry, relativePath);
            } else if (entry.kind === "file") {
                files.push({
                    file: await entry.getFile(),
                    relativePath: `${directoryHandle.name}/${relativePath}`
                });
            }
        }
    };
    await walk(directoryHandle);
    return files;
}

const getDirectoryPermission = async (directoryHandle, requestPermission) => {
    if (!directoryHandle) {
        return "unavailable";
    }
    if (typeof directoryHandle.queryPermission !== "function") {
        return "granted";
    }
    let permission = await directoryHandle.queryPermission({mode: "read"});
    if (
        permission === "prompt" &&
        requestPermission &&
        typeof directoryHandle.requestPermission === "function"
    ) {
        permission = await directoryHandle.requestPermission({mode: "read"});
    }
    return permission;
};

export async function hydrateUserLibraryFromDirectory(
    library,
    {requestPermission = false} = {}
) {
    if (library?.storageMode !== "directory" || !library.directoryHandle) {
        return {
            ...library,
            permissionState: library?.storageMode === "directory"
                ? "unavailable"
                : "local-copy"
        };
    }
    const permissionState = await getDirectoryPermission(
        library.directoryHandle,
        requestPermission
    );
    if (permissionState !== "granted") {
        return {
            ...library,
            files: [],
            permissionState
        };
    }
    const files = await collectFilesFromDirectoryHandle(library.directoryHandle);
    const hydrated = createUserLibraryRecord(library.kind, files, {
        id: library.id,
        name: library.name,
        createdAt: library.createdAt,
        directoryHandle: library.directoryHandle,
        storageMode: "directory"
    });
    return hydrateUserLibraryThumbnails(restoreBackgroundLinkOverrides(hydrated, library));
}

const requestResult = (request) => new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Browser storage request failed."));
});

const openLibraryDatabase = (indexedDatabase = globalThis.indexedDB) =>
    new Promise((resolve, reject) => {
        if (!indexedDatabase) {
            reject(new Error("Persistent browser storage is unavailable."));
            return;
        }
        const request = indexedDatabase.open(LIBRARY_DATABASE_NAME, LIBRARY_DATABASE_VERSION);
        request.onupgradeneeded = () => {
            const database = request.result;
            if (!database.objectStoreNames.contains(LIBRARY_STORE_NAME)) {
                database.createObjectStore(LIBRARY_STORE_NAME, {keyPath: "id"});
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error || new Error("Unable to open browser storage."));
    });

const withLibraryStore = async (mode, operation, indexedDatabase) => {
    const database = await openLibraryDatabase(indexedDatabase);
    try {
        const transaction = database.transaction(LIBRARY_STORE_NAME, mode);
        const store = transaction.objectStore(LIBRARY_STORE_NAME);
        return await operation(store);
    } finally {
        database.close();
    }
};

export const listUserLibraries = (indexedDatabase = globalThis.indexedDB) =>
    withLibraryStore("readonly", async store => {
        const libraries = await requestResult(store.getAll());
        return (libraries || []).sort((a, b) =>
            String(a.name).localeCompare(String(b.name))
        );
    }, indexedDatabase);

export const saveUserLibrary = (library, indexedDatabase = globalThis.indexedDB) =>
    withLibraryStore("readwrite", store =>
        requestResult(store.put(libraryForStorage(library))), indexedDatabase);

export const deleteUserLibrary = (libraryId, indexedDatabase = globalThis.indexedDB) =>
    withLibraryStore("readwrite", store =>
        requestResult(store.delete(libraryId)), indexedDatabase);

const fileByPath = (library, path) =>
    (library?.files || []).find(file => file.path === path);

export function buildModelSelectionFromLibrary(library, asset) {
    if (!library || !["model", "background"].includes(library.kind) || !asset) {
        return null;
    }
    const mainFile = fileByPath(library, asset.mainPath);
    if (!mainFile?.blob) {
        return null;
    }
    const mainDirectory = dirname(asset.mainPath);
    const additionalFiles = [];
    const registeredPaths = new Set();
    const register = (path, blob) => {
        const normalized = normalizePath(path);
        if (!normalized || registeredPaths.has(normalized)) {
            return;
        }
        registeredPaths.add(normalized);
        additionalFiles.push([normalized, blob]);
    };
    (library.files || []).forEach(file => {
        if (!file.blob || file.path === asset.mainPath) {
            return;
        }
        register(file.path, file.blob);
        if (mainDirectory && file.path.startsWith(mainDirectory)) {
            register(file.path.slice(mainDirectory.length), file.blob);
        }
    });
    return {
        mainFile: [asset.mainPath, mainFile.blob],
        additionalFiles,
        source: `library:${library.id}`
    };
}

export function getLibraryAssetBlob(library, asset) {
    return fileByPath(library, asset?.mainPath)?.blob || null;
}

export function getLibraryThumbnailBlob(library, asset) {
    if (!asset) {
        return null;
    }
    if (asset.thumbnailPath) {
        return fileByPath(library, asset.thumbnailPath)?.blob ||
            asset.cachedThumbnailBlob ||
            null;
    }
    return asset.cachedThumbnailBlob || asset.embeddedThumbnailBlob || null;
}

export const userEnvironmentKey = (libraryId, assetId) =>
    `user-library:${libraryId}:${assetId}`;
